import { useState } from "react";
import { FiX, FiRefreshCw } from "react-icons/fi";
import { Button } from "../../../components";

/**
 * RerunConfirmDialog — confirms a re-run onto the current model. Shows the
 * changelog (what re-running changes) and calls the page-supplied `onConfirm`,
 * which queues a fresh run via the existing project/load queue endpoint.
 *
 * The dialog is decoupled from projects vs loads: the parent passes `onConfirm`
 * (e.g. `() => queueLoadRun(loadId, token)`), then closes and lets the page's
 * existing polling pick up the new run.
 *
 * Copy is deliberately honest: re-running produces a NEW result on the current
 * model; the existing result is preserved (a new model_runs row is created).
 */
interface RerunConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Queues the re-run. May be async; the dialog shows a spinner until it
   *  resolves, then closes. */
  onConfirm: () => Promise<unknown> | unknown;
  /** What re-running would change, from the run's version status. */
  changelog?: string | null;
  /** Label the current result was produced on. */
  resultModelVersion?: string | null;
  /** Current release label the re-run will produce. */
  currentModelVersion?: string | null;
}

export function RerunConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  changelog,
  resultModelVersion,
  currentModelVersion,
}: RerunConfirmDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Failed to queue re-run. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-surface-700 rounded-lg shadow-xl border border-border w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-medium text-text-primary flex items-center gap-2">
            <FiRefreshCw size={18} className="text-health-300" />
            Re-run on the latest model
          </h2>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-text-muted hover:text-text-primary disabled:opacity-50"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded p-3">
              {error}
            </div>
          )}

          <div className="text-body-sm text-text-secondary">
            <span className="text-text-muted">
              {resultModelVersion ? (
                <>
                  This result was computed on{" "}
                  <span className="font-medium text-text-secondary">
                    {resultModelVersion}
                  </span>
                  .{" "}
                </>
              ) : null}
            </span>
            Re-running produces a{" "}
            <span className="font-medium text-text-primary">new result</span> on
            the current model
            {currentModelVersion ? (
              <>
                {" "}
                (
                <span className="font-medium text-text-primary">
                  {currentModelVersion}
                </span>
                )
              </>
            ) : null}
            . Your existing result is preserved.
          </div>

          {changelog && (
            <div className="rounded-lg border border-health-500/30 bg-health-500/5 p-4">
              <p className="text-caption font-medium uppercase tracking-wide text-health-300 mb-1.5">
                What re-running changes
              </p>
              <p className="text-body-sm text-text-secondary whitespace-pre-line">
                {changelog}
              </p>
            </div>
          )}

          <p className="text-caption text-text-muted">
            The new run is queued now; this page will update automatically when
            it completes.
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={submitting}
            icon={<FiRefreshCw size={14} />}
          >
            Re-run on latest
          </Button>
        </div>
      </div>
    </div>
  );
}
